// src/pages/Lawyer/LawyerForm.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IMaskInput } from "react-imask";
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Badge,
  Accordion,
  ProgressBar,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useFormik, FieldArray, FormikProvider } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import {
  Save,
  CornerDownLeft,
  ShieldCheck,
  Plus,
  Trash2,
  CheckCircle2,
  CircleDot,
} from "lucide-react";

import AlertMessage from "../../components/Common/AlertMessage";
import extractApiErrors from "../../utils/extractApiErrors";
import SelectCategory from "../../components/Common/SelectCategory";
import {
  createLawyer,
  updateLawyer,
  getLawyerById,
} from "../../services/lawyerService";
import SelectLawyer from "../../components/Common/SelectLawyer";

import SelectRolesMulti from "../../components/Common/SelectRolesMulti";
import { getAllRoles } from "../../services/securityRoleService";
import SelectCountry from "../../components/Common/SelectCountry";
import SelectDepartment from "../../components/Common/SelectDepartment";
import SelectMunicipality from "../../components/Common/SelectMunicipality";
import { useAuth } from "../../context/AuthContext";

// --------------------------- Shell + UI helpers ---------------------------

const StickyHeader = ({
  title,
  subtitle,
  breadcrumb,
  onBack,
  onSave,
  disabled,
  progress,
}) => (
  <div className="bg-body border-bottom position-sticky top-0 z-3">
    <Container className="py-3">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div className="min-w-0">
          {breadcrumb?.length > 0 && (
            <nav aria-label="breadcrumb" className="mb-1">
              <ol className="breadcrumb mb-0">
                {breadcrumb.map((b, i) => (
                  <li
                    key={i}
                    className={`breadcrumb-item ${b.active ? "active" : ""}`}
                    {...(!b.active
                      ? { onClick: b.onClick, role: "button" }
                      : {})}
                    style={!b.active ? { cursor: "pointer" } : {}}
                  >
                    {b.label}
                  </li>
                ))}
              </ol>
            </nav>
          )}
          <div className="d-flex align-items-center gap-2">
            <h3 className="mb-0 text-truncate">{title}</h3>
          </div>
          {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            variant="secondary"
            onClick={onBack}
            className="d-flex align-items-center gap-2"
          >
            <CornerDownLeft size={16} /> Volver
          </Button>
          <Button
            onClick={onSave}
            disabled={disabled}
            className="d-flex align-items-center gap-2"
          >
            <Save size={16} /> Guardar
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <ProgressBar now={progress} style={{ height: 6 }} />
      </div>
    </Container>
  </div>
);

// --------------------------- Validación ---------------------------

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const minStart = new Date(1900, 0, 2); // > 1900-01-01
const toNullIfEmpty = (v, og) => (og === "" || og === undefined ? null : v);

const validationSchema = (isCreate, isStudent) =>
  Yup.object().shape({
    securityUser: Yup.object().shape({
      firstName: Yup.string()
        .required("El/los nombre(s) son obligatorios.")
        .max(100, "El/los nombre(s) no pueden exceder los 100 caracteres.")
        .test(
          "no-leading-trailing-spaces",
          "El/los nombre(s) no debe(n) tener espacios al inicio o al final.",
          (value) => value === value?.trim()
        )
        .matches(
          /^[\p{L}\p{M}'\- ]+$/u,
          "El/los nombre(s) solo puede(n) contener letras y espacios."
        ),
      lastName: Yup.string()
        .required("El/los apellidos son obligatorios.")
        .max(100, "El/los apellidos no pueden exceder los 100 caracteres.")
        .test(
          "no-leading-trailing-spaces",
          "El/los apellidos no debe(n) tener espacios al inicio o al final.",
          (value) => value === value?.trim()
        )
        .matches(
          /^[\p{L}\p{M}'\- ]+$/u,
          "El/los apellidos solo puede(n) contener letras y espacios."
        ),
      email: Yup.string()
        .email("Correo inválido")
        .required("Correo es obligatorio")
        .trim(),

      roleIds: Yup.array()
        .transform((val) => {
          if (typeof val === "string")
            return val.split(",").map((x) => x.trim());
          if (Array.isArray(val)) {
            return val
              .map((x) => (typeof x === "string" ? x : x?.value ?? ""))
              .filter(Boolean);
          }
          return [];
        })
        .of(Yup.string().trim().min(1, "Rol inválido"))
        .min(1, "Debe seleccionar al menos un rol")
        .required("Debe seleccionar al menos un rol"),

      ...(isCreate
        ? {
            password: Yup.string()
              .required("La contraseña es obligatoria.")
              .min(6, "La contraseña debe tener al menos 6 caracteres.")
              .max(40, "La contraseña no debe superar los 40 caracteres.")
              .matches(
                /[A-Z]+/,
                "La contraseña debe contener al menos una letra mayúscula."
              )
              .matches(/\d+/, "La contraseña debe contener al menos un número.")
              .matches(
                /[!@#$%^&*(),.?":{}|<>]/,
                "La contraseña debe contener al menos un carácter especial."
              ),
            confirmPassword: Yup.string()
              .required("Confirmación es obligatoria")
              .oneOf([Yup.ref("password")], "Las contraseñas no coinciden"),
          }
        : {
            password: Yup.string()
              .nullable()
              .min(6, "La contraseña debe tener al menos 6 caracteres.")
              .max(40, "La contraseña no debe superar los 40 caracteres.")
              .matches(
                /[A-Z]+/,
                "La contraseña debe contener al menos una letra mayúscula."
              )
              .matches(/\d+/, "La contraseña debe contener al menos un número.")
              .matches(
                /[!@#$%^&*(),.?":{}|<>]/,
                "La contraseña debe contener al menos un carácter especial."
              ),
            confirmPassword: Yup.string()
              .nullable()
              .oneOf([Yup.ref("password")], "Las contraseñas no coinciden"),
          }),
    }),

    documentTypeId: Yup.string()
      .uuid()
      .required("Tipo de documento es obligatorio"),
    documentNumber: Yup.string()
      .required("El número de documento es obligatorio.")
      .max(50, "El número de documento no debe superar 50 caracteres.")
      .matches(/^\d+$/, "El número de documento debe contener solo números.")
      .test(
        "no-leading-trailing-spaces",
        "El número de documento no debe tener espacios al inicio o al final.",
        (value) => (typeof value === "string" ? value === value.trim() : true)
      ),

    birthDate: Yup.date()
      .nullable()
      .typeError("La fecha de nacimiento no es válida.")
      .required("La fecha de nacimiento es obligatoria.")
      .max(new Date(), "La fecha de nacimiento debe ser una fecha pasada.")
      .test("min-age", "Debe tener al menos 15 años de edad.", (value) => {
        if (!value) return false;
        const t = new Date();
        const minDate = new Date(
          t.getFullYear() - 15,
          t.getMonth(),
          t.getDate()
        );
        return value <= minDate;
      }),

    genderId: Yup.string().uuid().required("Género es obligatorio"),

    phoneMobile: Yup.string()
      .required("El número de celular es obligatorio.")
      .matches(
        /^3\d{9}$/,
        "El número de celular debe iniciar en 3 y tener exactamente 10 dígitos."
      )
      .trim(),

    phoneAlt: Yup.string()
      .nullable()
      .max(30, "El teléfono alternativo no debe superar 30 caracteres.")
      .test(
        "optional",
        "El teléfono alternativo debe tener entre 7 y 15 dígitos (con o sin prefijo +).",
        (value) => !value || value.trim() === "" || /^\+?\d{7,15}$/.test(value)
      ),

    addressLine1: Yup.string()
      .required("La dirección principal es obligatoria.")
      .max(250, "La dirección no debe superar los 250 caracteres.")
      .matches(
        /^(?:(?:Calle|Cra|Carrera|Transversal|Diagonal|Circular|Av|Avenida|Autopista)\s+\d+[A-Z]?\s*(?:#|No\.?|N°)\s*\d+[A-Z]?\s*-\s*\d+[A-Z]?)$/i,
        "La dirección debe tener un formato válido en Colombia (ejemplo: 'Calle 45 # 32-15')."
      ),

    addressLine2: Yup.string().nullable(),

    countryId: Yup.string().uuid().required("País es obligatorio"),
    departmentId: Yup.string().uuid().required("Departamento es obligatorio"),
    municipalityId: Yup.string().uuid().required("Municipio es obligatorio"),

    academicLevelId: Yup.string()
      .uuid()
      .required("Nivel académico es obligatorio"),

    // ✅ Tutor requerido solo si es estudiante
    tutorLawyerId: isStudent
      ? Yup.string()
          .uuid()
          .required("Si el nivel es ESTUDIANTE, el tutor es obligatorio.")
      : Yup.string().nullable(),

    mainDegreeTitle: Yup.string()
      .required("El título principal es obligatorio.")
      .min(5, "Debe tener al menos 5 caracteres.")
      .max(100, "No debe superar los 100 caracteres.")
      .trim()
      .matches(
        /^[\p{L}\p{N}\s\.,\-()°]+$/u,
        "El título contiene caracteres inválidos."
      ),

    mainInstitution: Yup.string()
      .required("La institución académica es obligatoria.")
      .min(3, "Debe tener al menos 3 caracteres.")
      .max(150, "No debe superar los 150 caracteres.")
      .trim()
      .matches(
        /^[\p{L}\p{N}\s\.,\-()°]+$/u,
        "La institución contiene caracteres inválidos."
      ),

    // ✅ Graduación principal NO aplica si es estudiante
    mainGraduationDate: isStudent
      ? Yup.date().nullable().transform(toNullIfEmpty)
      : Yup.date()
          .required("La fecha de graduación es obligatoria.")
          .min(new Date(1950, 0, 1), "No puede ser anterior a 1950.")
          .max(new Date(), "Debe ser una fecha pasada."),

    // ✅ Licencia NO aplica si es estudiante
    professionalLicenseNumber: isStudent
      ? Yup.string().nullable()
      : Yup.string()
          .required("El número de tarjeta profesional es obligatorio.")
          .min(6, "Debe tener al menos 6 caracteres.")
          .max(20, "No debe superar los 20 caracteres.")
          .matches(
            /^[A-Za-z0-9]+$/,
            "Solo letras (sin acentos) y números, sin espacios ni caracteres especiales."
          )
          .test(
            "no-trim-spaces",
            "No debe contener espacios al inicio o final.",
            (value) => !value || value.trim() === value
          ),

    professionalLicenseIssueDate: isStudent
      ? Yup.date().nullable().transform(toNullIfEmpty)
      : Yup.date()
          .required("La fecha de expedición es obligatoria.")
          .max(new Date(), "Debe ser una fecha pasada."),

    ProfessionalLicenseIssuedBy: isStudent
      ? Yup.string().nullable()
      : Yup.string()
          .required(
            "La entidad que expidió la tarjeta profesional es obligatoria."
          )
          .min(3, "La entidad debe tener al menos 3 caracteres.")
          .max(150, "La entidad no debe superar los 150 caracteres.")
          .test(
            "no-trim-spaces",
            "La entidad no debe tener espacios al inicio o al final.",
            (value) => !value || value.trim() === value
          )
          .matches(
            /^[\p{L}\p{N}\s\.,\-()°]+$/u,
            "La entidad contiene caracteres inválidos."
          ),

    IsLicenseVerified: isStudent
      ? Yup.boolean().nullable()
      : Yup.boolean().required(),

    summary: Yup.string().nullable(),
    specialtyIds: Yup.array().of(Yup.string().uuid()).nullable(),

    educations: Yup.array()
      .of(
        Yup.object()
          .shape({
            titleName: Yup.string()
              .required("El título del grado académico es obligatorio.")
              .min(3, "El título debe tener al menos 3 caracteres.")
              .max(200, "El título no debe superar los 200 caracteres.")
              .test(
                "no-trim-spaces",
                "El título no debe contener espacios al inicio o al final.",
                (v) => !v || v.trim() === v
              ),

            institution: Yup.string()
              .required("La institución es obligatoria.")
              .min(3, "La institución debe tener al menos 3 caracteres.")
              .max(200, "La institución no debe superar los 200 caracteres.")
              .test(
                "no-trim-spaces",
                "La institución no debe contener espacios al inicio o al final.",
                (v) => !v || v.trim() === v
              ),

            academicLevelId: Yup.string()
              .uuid()
              .required("Nivel académico es obligatorio"),

            startDate: Yup.date()
              .transform(toNullIfEmpty)
              .typeError("La fecha de inicio es inválida.")
              .required("La fecha de inicio es obligatoria.")
              .max(today(), "La fecha de inicio no puede estar en el futuro.")
              .min(minStart, "La fecha de inicio debe ser posterior a 1900."),

            GraduationDate: Yup.date()
              .transform(toNullIfEmpty)
              .typeError("La fecha de finalización es inválida.")
              .nullable()
              .when("startDate", (start, schema) =>
                start
                  ? schema.min(
                      start,
                      "La fecha de finalización no puede ser anterior a la fecha de inicio."
                    )
                  : schema
              )
              .max(
                today(),
                "La fecha de finalización no puede ser una fecha futura."
              )
              .nullable(),

            IsGraduated: Yup.boolean().required(),
          })
          .test("duration-min-3-months", function (val) {
            const { startDate, GraduationDate } = val || {};
            if (!startDate || !GraduationDate) return true;

            const start = new Date(startDate);
            const minEnd = new Date(start);
            minEnd.setMonth(minEnd.getMonth() + 3);

            if (new Date(GraduationDate) >= minEnd) return true;

            return this.createError({
              path: `${this.path}.GraduationDate`,
              message:
                "La duración de la carrera debe ser de al menos 3 meses.",
            });
          })
          .test("hasDateThenGraduated", function (val) {
            const { GraduationDate, IsGraduated } = val || {};
            if (!GraduationDate || !!IsGraduated) return true;
            return this.createError({
              path: `${this.path}.IsGraduated`,
              message:
                "Si se indicó una fecha de finalización, marca 'Graduado'.",
            });
          })
          .test("graduatedNeedsDate", function (val) {
            const { GraduationDate, IsGraduated } = val || {};
            if (!IsGraduated || !!GraduationDate) return true;
            return this.createError({
              path: `${this.path}.GraduationDate`,
              message: "Si está graduado, indica la fecha de finalización.",
            });
          })
          .test("noDateNoGraduated", function (val) {
            const { GraduationDate, IsGraduated } = val || {};
            if (!!GraduationDate || !IsGraduated) return true;
            return this.createError({
              path: `${this.path}.IsGraduated`,
              message:
                "No puedes marcar 'Graduado' sin indicar la fecha de finalización.",
            });
          })
      )
      .default([]),
  });

// --------------------------- Iniciales ---------------------------

const initialValuesBase = {
  securityUser: {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    UserType: 3,
    roleIds: [],
  },
  documentTypeId: "",
  documentNumber: "",
  birthDate: "",
  genderId: "",
  phoneMobile: "",
  phoneAlt: "",
  addressLine1: "",
  addressLine2: "",
  countryId: "",
  departmentId: "",
  municipalityId: "",

  academicLevelId: "",
  tutorLawyerId: "",
  mainDegreeTitle: "",
  mainInstitution: "",
  mainGraduationDate: "",

  professionalLicenseNumber: "",
  professionalLicenseIssueDate: "",
  ProfessionalLicenseIssuedBy: "",
  IsLicenseVerified: false,

  summary: "",
  specialtyIds: [],

  educations: [],
};

// --------------------------- Secciones (para sidebar/accordion) ---------------------------

const SECTION_LIST = [
  { key: "cuenta", label: "Cuenta" },
  { key: "identidad", label: "Identidad" },
  { key: "contacto", label: "Contacto" },
  { key: "ubicacion", label: "Ubicación" },
  { key: "profesional", label: "Profesional" },
  { key: "educacion", label: "Educación" },
  { key: "resumen", label: "Resumen" },
];

// --------------------------- Page ---------------------------

const LawyerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isCreate = !id;

  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  // refs para scroll suave a una sección desde la barra lateral
  const sectionRefs = useRef(
    SECTION_LIST.reduce((acc, s) => {
      acc[s.key] = React.createRef();
      return acc;
    }, {})
  );

  // Snapshot de geografía y "previous" para detectar cambios reales
  const geoSnapshotRef = useRef({
    countryId: "",
    departmentId: "",
    municipalityId: "",
  });
  const prevCountryId = useRef("");
  const prevDeptId = useRef("");

  const isFilled = (v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "string") return v.trim() !== "";
    return v !== null && v !== undefined && v !== "";
  };

  // estado de acordeón: múltiples abiertos
  const [openKeys, setOpenKeys] = useState(["cuenta"]);

  const toggleKey = (k) =>
    setOpenKeys((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );

  const openAndScroll = (k) => {
    setOpenKeys((prev) => (prev.includes(k) ? prev : [...prev, k]));
    const el = sectionRefs.current[k]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const { user } = useAuth();
  const companyId = user?.companyId;

  // academic level item (para detectar ESTUDIANTE)
  const [academicLevelItem, setAcademicLevelItem] = useState(null);

  const isStudent = useMemo(() => {
    const code = (academicLevelItem?.code || "")
      .toString()
      .trim()
      .toUpperCase();
    if (code) return code === "ESTUDIANTE";

    const name = (academicLevelItem?.name || "")
      .toString()
      .trim()
      .toUpperCase();
    return name === "ESTUDIANTE";
  }, [academicLevelItem]);

  const formik = useFormik({
    initialValues: initialValuesBase,
    validationSchema: validationSchema(isCreate, isStudent),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      // Snapshot de los IDs geo antes de enviar
      geoSnapshotRef.current = {
        countryId: values.countryId,
        departmentId: values.departmentId,
        municipalityId: values.municipalityId,
      };

      try {
        const payload = {
          id: isCreate ? undefined : id,
          securityUser: {
            id: values.securityUser.id,
            isActive: true,
            CompanyId: companyId,
            firstName: values.securityUser.firstName,
            lastName: values.securityUser.lastName,
            email: values.securityUser.email,
            password: isCreate
              ? values.securityUser.password
              : values.securityUser.password || undefined,
            confirmPassword: isCreate
              ? values.securityUser.confirmPassword
              : values.securityUser.confirmPassword || undefined,
            UserType: 3,
            roleIds: values.securityUser.roleIds,
          },

          documentTypeId: values.documentTypeId,
          documentNumber: values.documentNumber,
          birthDate: values.birthDate || null,
          genderId: values.genderId,
          phoneMobile: values.phoneMobile,
          phoneAlt: values.phoneAlt || "",
          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2 || "",
          isActive: true,
          countryId: values.countryId,
          departmentId: values.departmentId,
          municipalityId: values.municipalityId,

          academicLevelId: values.academicLevelId,

          // Tutor
          tutorLawyerId: isStudent ? values.tutorLawyerId : null,

          mainDegreeTitle: values.mainDegreeTitle,
          mainInstitution: values.mainInstitution,
          mainGraduationDate: isStudent
            ? null
            : values.mainGraduationDate || null,

          // Licencia se anula si es estudiante
          professionalLicenseNumber: isStudent
            ? null
            : values.professionalLicenseNumber,
          professionalLicenseIssueDate: isStudent
            ? null
            : values.professionalLicenseIssueDate || null,
          ProfessionalLicenseIssuedBy: isStudent
            ? null
            : values.ProfessionalLicenseIssuedBy,
          IsLicenseVerified: isStudent ? false : !!values.IsLicenseVerified,

          summary: values.summary || "",
          specialtyIds: values.specialtyIds || [],
          educations: (values.educations || []).map((e) => ({
            titleName: e.titleName,
            institution: e.institution,
            academicLevelId: e.academicLevelId,
            startDate: e.startDate || null,
            GraduationDate: e.GraduationDate || null,
            IsGraduated: !!e.IsGraduated,
          })),
        };

        let response;
        if (isCreate) response = await createLawyer(payload);
        else response = await updateLawyer(id, payload);

        if (!response?.success) throw { backend: response };

        setSuccessMessage(
          isCreate
            ? "✅ Abogado creado correctamente."
            : "✅ Abogado actualizado correctamente."
        );

        Swal.fire({
          toast: true,
          position: "top",
          icon: "success",
          title: isCreate ? "Abogado creado" : "Cambios guardados",
          timer: 1800,
          showConfirmButton: false,
        });

        setTimeout(() => navigate("/dashboard/lawyers"), 1200);
      } catch (error) {
        const parsedErrors = extractApiErrors(error);
        setErrors(parsedErrors);

        // Reponer selección geo si algún re-montaje limpió
        const snap = geoSnapshotRef.current;
        if (snap) {
          formik.setFieldValue("countryId", snap.countryId, false);
          formik.setFieldValue("departmentId", snap.departmentId, false);
          formik.setFieldValue("municipalityId", snap.municipalityId, false);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ✅ al cambiar isStudent: limpiar campos que no aplican
  useEffect(() => {
    if (isStudent) {
      formik.setFieldValue("professionalLicenseNumber", "", false);
      formik.setFieldValue("professionalLicenseIssueDate", "", false);
      formik.setFieldValue("ProfessionalLicenseIssuedBy", "", false);
      formik.setFieldValue("IsLicenseVerified", false, false);
      formik.setFieldValue("mainGraduationDate", "", false);
    } else {
      formik.setFieldValue("tutorLawyerId", "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent]);

  // Limpiar dependientes SOLO si hay cambio real de país/departamento
  useEffect(() => {
    const current = formik.values.countryId || "";
    if (prevCountryId.current && prevCountryId.current !== current) {
      formik.setFieldValue("departmentId", "");
      formik.setFieldValue("municipalityId", "");
    }
    prevCountryId.current = current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.countryId]);

  useEffect(() => {
    const current = formik.values.departmentId || "";
    if (prevDeptId.current && prevDeptId.current !== current) {
      formik.setFieldValue("municipalityId", "");
    }
    prevDeptId.current = current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.departmentId]);

  // Cargar datos si es edición
  useEffect(() => {
    if (!isCreate) {
      getLawyerById(id)
        .then((r) => {
          if (!r?.success) throw new Error("No se pudo cargar el abogado.");
          const d = r.data;

          // 🔥 intentar setear academicLevelItem si backend lo trae
          setAcademicLevelItem({
            id: d?.academicLevelId,
            code: d?.academicLevelCode,
            name: d?.academicLevelName,
          });

          formik.setValues({
            ...initialValuesBase,
            securityUser: {
              id: d?.securityUserId || "",
              isActive: true,
              firstName: d?.firstName || "",
              lastName: d?.lastName || "",
              email: d?.email || "",
              password: "",
              confirmPassword: "",
              UserType: 3,
              roleIds: Array.isArray(d?.roleIds) ? d.roleIds : [],
            },
            documentTypeId: d?.documentTypeId || "",
            documentNumber: d?.documentNumber || "",
            birthDate: d?.birthDate ? d.birthDate.substring(0, 10) : "",
            genderId: d?.genderId || "",
            phoneMobile: d?.phoneMobile || "",
            phoneAlt: d?.phoneAlt || "",
            addressLine1: d?.addressLine1 || "",
            addressLine2: d?.addressLine2 || "",
            countryId: d?.countryId || "",
            departmentId: d?.departmentId || "",
            municipalityId: d?.municipalityId || "",

            academicLevelId: d?.academicLevelId || "",
            tutorLawyerId: d?.tutorLawyerId || "",

            mainDegreeTitle: d?.mainDegreeTitle || "",
            mainInstitution: d?.mainInstitution || "",
            mainGraduationDate: d?.mainGraduationDate
              ? d.mainGraduationDate.substring(0, 10)
              : "",

            professionalLicenseNumber: d?.professionalLicenseNumber || "",
            professionalLicenseIssueDate: d?.professionalLicenseIssueDate
              ? d.professionalLicenseIssueDate.substring(0, 10)
              : "",
            ProfessionalLicenseIssuedBy: d?.professionalLicenseIssuedBy || "",
            IsLicenseVerified: !!d?.isLicenseVerified,

            summary: d?.summary || "",
            specialtyIds: Array.isArray(d?.specialtyIds) ? d.specialtyIds : [],
            educations: Array.isArray(d?.educations)
              ? d.educations.map((e) => ({
                  titleName: e?.titleName || "",
                  institution: e?.institution || "",
                  academicLevelId: e?.academicLevelId || "",
                  startDate: e?.startDate ? e.startDate.substring(0, 10) : "",
                  GraduationDate: e?.graduationDate
                    ? e.graduationDate.substring(0, 10)
                    : "",
                  IsGraduated: !!e?.isGraduated,
                }))
              : [],
          });
        })
        .catch(() => {
          Swal.fire("Error", "No se pudo cargar el abogado", "error");
          navigate("/dashboard/lawyers");
        });
    }
  }, [id, isCreate, navigate]); // eslint-disable-line

  // Hotkeys
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        formik.handleSubmit();
      }
      if (e.key === "Escape") navigate("/dashboard/lawyers");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [formik, navigate]);

  // Progreso + estado secciones (dinámico por isStudent)
  const requiredBySection = useMemo(() => {
    const profesionalBase = [
      "academicLevelId",
      "mainDegreeTitle",
      "mainInstitution",
    ];
    const profesionalReq = isStudent
      ? [...profesionalBase, "tutorLawyerId"]
      : [
          ...profesionalBase,
          "professionalLicenseNumber",
          "ProfessionalLicenseIssuedBy",
        ];

    return {
      cuenta: [
        "securityUser.roleIds",
        "securityUser.firstName",
        "securityUser.lastName",
        "securityUser.email",
        ...(isCreate
          ? ["securityUser.password", "securityUser.confirmPassword"]
          : []),
      ],
      identidad: ["documentTypeId", "documentNumber", "genderId"],
      contacto: ["phoneMobile"],
      ubicacion: [
        "addressLine1",
        "countryId",
        "departmentId",
        "municipalityId",
      ],
      profesional: profesionalReq,
      educacion: [],
      resumen: [],
    };
  }, [isCreate, isStudent]);

  const sectionIsComplete = (key) =>
    (requiredBySection[key] || []).every((path) => {
      const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
      return isFilled(v);
    });

  const allRequired = Object.keys(requiredBySection).flatMap(
    (k) => requiredBySection[k]
  );

  const pendingCount = allRequired.filter((path) => {
    const v = path.split(".").reduce((acc, p) => acc?.[p], formik.values);
    return !isFilled(v);
  }).length;

  const progress = Math.round(
    (1 - pendingCount / (allRequired.length || 1)) * 100
  );

  const isInvalid = (name) => formik.touched[name] && !!formik.errors[name];
  const errorMsg = (name) => (formik.touched[name] ? formik.errors[name] : "");

  const [rolesOptions, setRolesOptions] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const roles = await getAllRoles();
        if (mounted) {
          const mapped = roles.map((r) => ({
            value: r.id,
            label: r.name,
            raw: r,
          }));
          setRolesOptions(mapped);
        }
      } catch (e) {
        console.error("Error cargando roles:", e);
        if (mounted) setRolesOptions([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <StickyHeader
        title={isCreate ? "Crear abogado" : "Editar abogado"}
        breadcrumb={[]}
        onBack={() => navigate("/dashboard/lawyers")}
        onSave={formik.handleSubmit}
        disabled={formik.isSubmitting}
        progress={progress}
      />

      <div className="container-fluid mt-4 px-4 w-100">
        <Row className="g-4">
          {/* Sidebar con navegación de secciones */}
          <Col lg={3}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="fw-semibold">Secciones</div>
                  <Badge bg={pendingCount ? "warning" : "success"}>
                    {pendingCount
                      ? `${pendingCount} campos pendientes`
                      : "Completo"}
                  </Badge>
                </div>

                <div className="list-group list-group-flush">
                  {SECTION_LIST.map((s) => {
                    const complete = sectionIsComplete(s.key);
                    const open = openKeys.includes(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        className={`list-group-item list-group-item-action d-flex align-items-center justify-content-between ${
                          open ? "active" : ""
                        }`}
                        onClick={() => openAndScroll(s.key)}
                      >
                        <span className="text-start">{s.label}</span>
                        {complete ? (
                          <CheckCircle2 size={18} className="text-success" />
                        ) : (
                          <CircleDot
                            size={16}
                            className="text-danger"
                            style={{ opacity: 0.7 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3 small text-muted d-flex align-items-center gap-2">
                  <ShieldCheck size={16} /> Puedes abrir múltiples secciones.
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contenido principal */}
          <Col lg={9}>
            <Card className="shadow-sm">
              <Card.Body>
                <FormikProvider value={formik}>
                  <Form noValidate onSubmit={formik.handleSubmit}>
                    <Accordion
                      alwaysOpen
                      activeKey={openKeys}
                      onSelect={() => {}}
                    >
                      {/* CUENTA */}
                      <Accordion.Item
                        eventKey="cuenta"
                        ref={sectionRefs.current.cuenta}
                      >
                        <Accordion.Header onClick={() => toggleKey("cuenta")}>
                          Cuenta{" "}
                          {sectionIsComplete("cuenta") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="roleIds">
                                <Form.Label>Roles *</Form.Label>
                                <SelectRolesMulti
                                  value={
                                    formik.values.securityUser.roleIds || []
                                  }
                                  onChange={(selected) =>
                                    formik.setFieldValue(
                                      "securityUser.roleIds",
                                      selected
                                    )
                                  }
                                  isDisabled={formik.isSubmitting}
                                  placeholder="Seleccione uno o más roles..."
                                  allOptions={rolesOptions}
                                />
                                {formik.touched.securityUser?.roleIds &&
                                  formik.errors.securityUser?.roleIds && (
                                    <div className="invalid-feedback d-block">
                                      {Array.isArray(
                                        formik.errors.securityUser.roleIds
                                      )
                                        ? formik.errors.securityUser.roleIds.join(
                                            ", "
                                          )
                                        : formik.errors.securityUser.roleIds}
                                    </div>
                                  )}
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="email">
                                <Form.Label>Correo *</Form.Label>
                                <Form.Control
                                  type="email"
                                  name="securityUser.email"
                                  value={formik.values.securityUser.email}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(
                                      formik.touched.securityUser?.email &&
                                      formik.errors.securityUser?.email
                                    )
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.securityUser?.email}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="firstName">
                                <Form.Label>Nombres *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="securityUser.firstName"
                                  value={formik.values.securityUser.firstName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(
                                      formik.touched.securityUser?.firstName &&
                                      formik.errors.securityUser?.firstName
                                    )
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.securityUser?.firstName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="lastName">
                                <Form.Label>Apellidos *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="securityUser.lastName"
                                  value={formik.values.securityUser.lastName}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    !!(
                                      formik.touched.securityUser?.lastName &&
                                      formik.errors.securityUser?.lastName
                                    )
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.securityUser?.lastName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            {isCreate && (
                              <>
                                <Col md={6}>
                                  <Form.Group controlId="password">
                                    <Form.Label>Contraseña *</Form.Label>
                                    <Form.Control
                                      type="password"
                                      name="securityUser.password"
                                      value={
                                        formik.values.securityUser.password
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={
                                        !!(
                                          formik.touched.securityUser
                                            ?.password &&
                                          formik.errors.securityUser?.password
                                        )
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {formik.errors.securityUser?.password}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="confirmPassword">
                                    <Form.Label>
                                      Confirmar contraseña *
                                    </Form.Label>
                                    <Form.Control
                                      type="password"
                                      name="securityUser.confirmPassword"
                                      value={
                                        formik.values.securityUser
                                          .confirmPassword
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={
                                        !!(
                                          formik.touched.securityUser
                                            ?.confirmPassword &&
                                          formik.errors.securityUser
                                            ?.confirmPassword
                                        )
                                      }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {
                                        formik.errors.securityUser
                                          ?.confirmPassword
                                      }
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>
                              </>
                            )}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* IDENTIDAD */}
                      <Accordion.Item
                        eventKey="identidad"
                        ref={sectionRefs.current.identidad}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("identidad")}
                        >
                          Identidad{" "}
                          {sectionIsComplete("identidad") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="documentTypeId">
                                <Form.Label>Tipo de documento *</Form.Label>
                                <SelectCategory
                                  categoryCode="document_type"
                                  showCode={false}
                                  value={formik.values.documentTypeId}
                                  onChange={(val) =>
                                    formik.setFieldValue("documentTypeId", val)
                                  }
                                  placeholder="Seleccione tipo de documento"
                                />
                                {isInvalid("documentTypeId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("documentTypeId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="documentNumber">
                                <Form.Label>Número de documento *</Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={50}
                                  name="documentNumber"
                                  value={formik.values.documentNumber}
                                  onChange={(e) => {
                                    const onlyDigits = e.target.value
                                      .replace(/\D/g, "")
                                      .slice(0, 50);
                                    formik.setFieldValue(
                                      "documentNumber",
                                      onlyDigits
                                    );
                                  }}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.documentNumber &&
                                    !!formik.errors.documentNumber
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.documentNumber}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="birthDate">
                                <Form.Label>Fecha de nacimiento *</Form.Label>
                                <Form.Control
                                  type="date"
                                  name="birthDate"
                                  value={
                                    formik.values.birthDate
                                      ? formik.values.birthDate.split("T")[0]
                                      : ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.birthDate &&
                                    !!formik.errors.birthDate
                                  }
                                  max={new Date().toISOString().split("T")[0]}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.birthDate}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="genderId">
                                <Form.Label>Género *</Form.Label>
                                <SelectCategory
                                  categoryCode="gender"
                                  showCode={false}
                                  value={formik.values.genderId}
                                  onChange={(val) =>
                                    formik.setFieldValue("genderId", val)
                                  }
                                  placeholder="Seleccione género"
                                />
                                {isInvalid("genderId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("genderId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* CONTACTO */}
                      <Accordion.Item
                        eventKey="contacto"
                        ref={sectionRefs.current.contacto}
                      >
                        <Accordion.Header onClick={() => toggleKey("contacto")}>
                          Contacto{" "}
                          {sectionIsComplete("contacto") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="phoneMobile">
                                <Form.Label>Celular *</Form.Label>
                                <Form.Control
                                  as={IMaskInput}
                                  mask="3000000000"
                                  name="phoneMobile"
                                  value={formik.values.phoneMobile}
                                  onAccept={(value) =>
                                    formik.setFieldValue("phoneMobile", value)
                                  }
                                  onBlur={formik.handleBlur}
                                  placeholder="3XXXXXXXXX"
                                  isInvalid={
                                    formik.touched.phoneMobile &&
                                    !!formik.errors.phoneMobile
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.phoneMobile}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="phoneAlt">
                                <Form.Label>Teléfono alternativo</Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="tel"
                                  maxLength={15}
                                  name="phoneAlt"
                                  value={formik.values.phoneAlt || ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^\d+]/g,
                                      ""
                                    );
                                    formik.setFieldValue("phoneAlt", val);
                                  }}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.phoneAlt &&
                                    !!formik.errors.phoneAlt
                                  }
                                  placeholder="+573001234567 o 6041234567"
                                />
                                <Form.Text className="text-muted">
                                  Entre 7 y 15 dígitos (puede incluir +).
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.phoneAlt}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* UBICACIÓN */}
                      <Accordion.Item
                        eventKey="ubicacion"
                        ref={sectionRefs.current.ubicacion}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("ubicacion")}
                        >
                          Ubicación{" "}
                          {sectionIsComplete("ubicacion") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>
                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="addressLine1">
                                <Form.Label>Dirección principal *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="addressLine1"
                                  value={formik.values.addressLine1}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.addressLine1 &&
                                    !!formik.errors.addressLine1
                                  }
                                  placeholder="Ej: Calle 45 # 32-15"
                                  maxLength={250}
                                />
                                <Form.Text className="text-muted">
                                  Ejemplo: Calle 45 # 32-15
                                </Form.Text>
                                <Form.Control.Feedback type="invalid">
                                  {formik.errors.addressLine1}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="addressLine2">
                                <Form.Label>Complemento</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="addressLine2"
                                  value={formik.values.addressLine2}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("addressLine2")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("addressLine2")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="countryId">
                                <Form.Label>País *</Form.Label>
                                <SelectCountry
                                  value={formik.values.countryId}
                                  onChange={(val) =>
                                    formik.setFieldValue("countryId", val)
                                  }
                                />
                                {isInvalid("countryId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("countryId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="departmentId">
                                <Form.Label>Departamento *</Form.Label>
                                <SelectDepartment
                                  countryId={formik.values.countryId}
                                  value={formik.values.departmentId}
                                  onChange={(val) =>
                                    formik.setFieldValue("departmentId", val)
                                  }
                                  isDisabled={!formik.values.countryId}
                                />
                                {isInvalid("departmentId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("departmentId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            <Col md={4}>
                              <Form.Group controlId="municipalityId">
                                <Form.Label>Municipio *</Form.Label>
                                <SelectMunicipality
                                  departmentId={formik.values.departmentId}
                                  value={formik.values.municipalityId}
                                  onChange={(val) =>
                                    formik.setFieldValue("municipalityId", val)
                                  }
                                  isDisabled={!formik.values.departmentId}
                                />
                                {isInvalid("municipalityId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("municipalityId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* PROFESIONAL */}
                      <Accordion.Item
                        eventKey="profesional"
                        ref={sectionRefs.current.profesional}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("profesional")}
                        >
                          Profesional{" "}
                          {sectionIsComplete("profesional") && (
                            <Badge bg="success" className="ms-2">
                              Completa
                            </Badge>
                          )}
                        </Accordion.Header>

                        <Accordion.Body>
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group controlId="academicLevelId">
                                <Form.Label>Nivel académico *</Form.Label>
                                <SelectCategory
                                  categoryCode="academic_level"
                                  showCode={false}
                                  value={formik.values.academicLevelId}
                                  onChange={(val) =>
                                    formik.setFieldValue("academicLevelId", val)
                                  }
                                  onSelectItem={(item) =>
                                    setAcademicLevelItem(item)
                                  } // ✅ CLAVE
                                  placeholder="Seleccione nivel"
                                />
                                {isInvalid("academicLevelId") && (
                                  <div className="invalid-feedback d-block">
                                    {errorMsg("academicLevelId")}
                                  </div>
                                )}
                              </Form.Group>
                            </Col>

                            {isStudent && (
                              <Col md={12}>
                                <Alert variant="info" className="mb-0">
                                  Este abogado está marcado como{" "}
                                  <strong>ESTUDIANTE</strong>. Se requiere tutor
                                  y no se solicita tarjeta profesional.
                                </Alert>
                              </Col>
                            )}
                            {isStudent && (
                              <Col md={12}>
                                <Form.Group controlId="tutorLawyerId">
                                  <Form.Label>Tutor responsable *</Form.Label>

                                  <SelectLawyer
                                    value={formik.values.tutorLawyerId || ""}
                                    disableStudents
                                    onChange={(val) =>
                                      formik.setFieldValue("tutorLawyerId", val)
                                    }
                                    isDisabled={formik.isSubmitting}
                                    placeholder="Seleccione un tutor..."
                                    excludeIds={[id].filter(Boolean)} // ✅ evita auto-tutor en edición
                                  />

                                  {formik.touched.tutorLawyerId &&
                                    formik.errors.tutorLawyerId && (
                                      <div className="invalid-feedback d-block">
                                        {formik.errors.tutorLawyerId}
                                      </div>
                                    )}
                                </Form.Group>
                              </Col>
                            )}

                            <Col md={6}>
                              <Form.Group controlId="mainDegreeTitle">
                                <Form.Label>Título principal *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="mainDegreeTitle"
                                  value={formik.values.mainDegreeTitle}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("mainDegreeTitle")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("mainDegreeTitle")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group controlId="mainInstitution">
                                <Form.Label>Institución *</Form.Label>
                                <Form.Control
                                  type="text"
                                  name="mainInstitution"
                                  value={formik.values.mainInstitution}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={isInvalid("mainInstitution")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errorMsg("mainInstitution")}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            {!isStudent && (
                              <Col md={6}>
                                <Form.Group controlId="mainGraduationDate">
                                  <Form.Label>Fecha de graduación</Form.Label>
                                  <Form.Control
                                    type="date"
                                    name="mainGraduationDate"
                                    value={
                                      formik.values.mainGraduationDate || ""
                                    }
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={isInvalid("mainGraduationDate")}
                                  />
                                  <Form.Control.Feedback type="invalid">
                                    {errorMsg("mainGraduationDate")}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            )}

                            {!isStudent && (
                              <>
                                <Col md={6}>
                                  <Form.Group controlId="professionalLicenseNumber">
                                    <Form.Label>
                                      Tarjeta/Licencia profesional *
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="professionalLicenseNumber"
                                      value={
                                        formik.values.professionalLicenseNumber
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid(
                                        "professionalLicenseNumber"
                                      )}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("professionalLicenseNumber")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={6}>
                                  <Form.Group controlId="professionalLicenseIssueDate">
                                    <Form.Label>Fecha expedición</Form.Label>
                                    <Form.Control
                                      type="date"
                                      name="professionalLicenseIssueDate"
                                      value={
                                        formik.values
                                          .professionalLicenseIssueDate || ""
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid(
                                        "professionalLicenseIssueDate"
                                      )}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("professionalLicenseIssueDate")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={8}>
                                  <Form.Group controlId="ProfessionalLicenseIssuedBy">
                                    <Form.Label>Expedido por *</Form.Label>
                                    <Form.Control
                                      type="text"
                                      name="ProfessionalLicenseIssuedBy"
                                      value={
                                        formik.values
                                          .ProfessionalLicenseIssuedBy
                                      }
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      isInvalid={isInvalid(
                                        "ProfessionalLicenseIssuedBy"
                                      )}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                      {errorMsg("ProfessionalLicenseIssuedBy")}
                                    </Form.Control.Feedback>
                                  </Form.Group>
                                </Col>

                                <Col md={4} className="d-flex align-items-end">
                                  <Form.Check
                                    type="switch"
                                    id="IsLicenseVerified"
                                    label="Licencia verificada"
                                    name="IsLicenseVerified"
                                    checked={!!formik.values.IsLicenseVerified}
                                    onChange={(e) =>
                                      formik.setFieldValue(
                                        "IsLicenseVerified",
                                        e.target.checked
                                      )
                                    }
                                  />
                                </Col>
                              </>
                            )}
                          </Row>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* EDUCACIÓN */}
                      <Accordion.Item
                        eventKey="educacion"
                        ref={sectionRefs.current.educacion}
                      >
                        <Accordion.Header
                          onClick={() => toggleKey("educacion")}
                        >
                          Educación
                        </Accordion.Header>
                        <Accordion.Body>
                          <FieldArray name="educations">
                            {({ push, remove }) => (
                              <>
                                {(formik.values.educations || []).map(
                                  (e, idx) => (
                                    <Card className="mb-3" key={idx}>
                                      <Card.Body>
                                        <Row className="g-3">
                                          <Col md={6}>
                                            <Form.Group>
                                              <Form.Label>Título *</Form.Label>
                                              <Form.Control
                                                type="text"
                                                name={`educations[${idx}].titleName`}
                                                value={
                                                  formik.values.educations[idx]
                                                    .titleName
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                  !!(
                                                    formik.touched.educations?.[
                                                      idx
                                                    ]?.titleName &&
                                                    formik.errors.educations?.[
                                                      idx
                                                    ]?.titleName
                                                  )
                                                }
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {
                                                  formik.errors.educations?.[
                                                    idx
                                                  ]?.titleName
                                                }
                                              </Form.Control.Feedback>
                                            </Form.Group>
                                          </Col>

                                          <Col md={6}>
                                            <Form.Group>
                                              <Form.Label>
                                                Institución *
                                              </Form.Label>
                                              <Form.Control
                                                type="text"
                                                name={`educations[${idx}].institution`}
                                                value={
                                                  formik.values.educations[idx]
                                                    .institution
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                  !!(
                                                    formik.touched.educations?.[
                                                      idx
                                                    ]?.institution &&
                                                    formik.errors.educations?.[
                                                      idx
                                                    ]?.institution
                                                  )
                                                }
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {
                                                  formik.errors.educations?.[
                                                    idx
                                                  ]?.institution
                                                }
                                              </Form.Control.Feedback>
                                            </Form.Group>
                                          </Col>

                                          <Col md={6}>
                                            <Form.Group>
                                              <Form.Label>
                                                Nivel académico *
                                              </Form.Label>
                                              <SelectCategory
                                                categoryCode="academic_level"
                                                showCode={false}
                                                value={
                                                  formik.values.educations[idx]
                                                    .academicLevelId
                                                }
                                                onChange={(val) =>
                                                  formik.setFieldValue(
                                                    `educations[${idx}].academicLevelId`,
                                                    val
                                                  )
                                                }
                                                placeholder="Seleccione nivel"
                                              />
                                              {!!(
                                                formik.touched.educations?.[idx]
                                                  ?.academicLevelId &&
                                                formik.errors.educations?.[idx]
                                                  ?.academicLevelId
                                              ) && (
                                                <div className="invalid-feedback d-block">
                                                  {
                                                    formik.errors.educations?.[
                                                      idx
                                                    ]?.academicLevelId
                                                  }
                                                </div>
                                              )}
                                            </Form.Group>
                                          </Col>

                                          <Col md={3}>
                                            <Form.Group>
                                              <Form.Label>Inicio *</Form.Label>
                                              <Form.Control
                                                type="date"
                                                name={`educations[${idx}].startDate`}
                                                value={
                                                  formik.values.educations[idx]
                                                    .startDate || ""
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                  !!(
                                                    formik.touched.educations?.[
                                                      idx
                                                    ]?.startDate &&
                                                    formik.errors.educations?.[
                                                      idx
                                                    ]?.startDate
                                                  )
                                                }
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {
                                                  formik.errors.educations?.[
                                                    idx
                                                  ]?.startDate
                                                }
                                              </Form.Control.Feedback>
                                            </Form.Group>
                                          </Col>

                                          <Col md={3}>
                                            <Form.Group>
                                              <Form.Label>
                                                Graduación
                                              </Form.Label>
                                              <Form.Control
                                                type="date"
                                                name={`educations[${idx}].GraduationDate`}
                                                value={
                                                  formik.values.educations[idx]
                                                    .GraduationDate || ""
                                                }
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={
                                                  !!(
                                                    formik.touched.educations?.[
                                                      idx
                                                    ]?.GraduationDate &&
                                                    formik.errors.educations?.[
                                                      idx
                                                    ]?.GraduationDate
                                                  )
                                                }
                                              />
                                              <Form.Control.Feedback type="invalid">
                                                {
                                                  formik.errors.educations?.[
                                                    idx
                                                  ]?.GraduationDate
                                                }
                                              </Form.Control.Feedback>
                                            </Form.Group>
                                          </Col>

                                          <Col
                                            md={3}
                                            className="d-flex align-items-end"
                                          >
                                            <div>
                                              <Form.Check
                                                type="switch"
                                                id={`educations-${idx}-IsGraduated`}
                                                label="Graduado"
                                                name={`educations[${idx}].IsGraduated`}
                                                checked={
                                                  !!formik.values.educations[
                                                    idx
                                                  ].IsGraduated
                                                }
                                                onChange={(e) => {
                                                  formik.setFieldValue(
                                                    `educations[${idx}].IsGraduated`,
                                                    e.target.checked
                                                  );
                                                  formik.setFieldTouched(
                                                    `educations[${idx}].IsGraduated`,
                                                    true,
                                                    false
                                                  );
                                                }}
                                              />
                                              {formik.touched.educations?.[idx]
                                                ?.IsGraduated &&
                                                formik.errors.educations?.[idx]
                                                  ?.IsGraduated && (
                                                  <div className="invalid-feedback d-block">
                                                    {
                                                      formik.errors.educations[
                                                        idx
                                                      ].IsGraduated
                                                    }
                                                  </div>
                                                )}
                                            </div>
                                          </Col>

                                          <Col
                                            md={3}
                                            className="d-flex align-items-end justify-content-end"
                                          >
                                            <Button
                                              variant="outline-danger"
                                              onClick={() => remove(idx)}
                                              className="d-flex align-items-center gap-2"
                                            >
                                              <Trash2 size={16} /> Quitar
                                            </Button>
                                          </Col>
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  )
                                )}

                                <Button
                                  variant="outline-primary"
                                  onClick={() =>
                                    push({
                                      titleName: "",
                                      institution: "",
                                      academicLevelId: "",
                                      startDate: "",
                                      GraduationDate: "",
                                      IsGraduated: false,
                                    })
                                  }
                                  className="d-flex align-items-center gap-2"
                                >
                                  <Plus size={16} /> Añadir formación
                                </Button>
                              </>
                            )}
                          </FieldArray>
                        </Accordion.Body>
                      </Accordion.Item>

                      {/* RESUMEN */}
                      <Accordion.Item
                        eventKey="resumen"
                        ref={sectionRefs.current.resumen}
                      >
                        <Accordion.Header onClick={() => toggleKey("resumen")}>
                          Resumen
                        </Accordion.Header>
                        <Accordion.Body>
                          <Form.Group controlId="summary">
                            <Form.Label>Resumen profesional</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={6}
                              name="summary"
                              value={formik.values.summary}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              isInvalid={isInvalid("summary")}
                              placeholder="Breve descripción del perfil y experiencia..."
                            />
                            <Form.Control.Feedback type="invalid">
                              {errorMsg("summary")}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>

                    {/* Alertas backend */}
                    <div className="mt-3">
                      {errors.length > 0 && (
                        <AlertMessage
                          type="error"
                          message={
                            <ul className="mb-0">
                              {errors.map((e, i) => (
                                <li key={i}>{e}</li>
                              ))}
                            </ul>
                          }
                          onClose={() => setErrors([])}
                        />
                      )}
                      {successMessage && (
                        <AlertMessage
                          type="success"
                          message={successMessage}
                          onClose={() => setSuccessMessage(null)}
                        />
                      )}
                    </div>

                    {/* Botones accesibles */}
                    <div className="d-flex justify-content-between mt-3">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/dashboard/lawyers")}
                      >
                        Volver
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={formik.isSubmitting}
                      >
                        {formik.isSubmitting
                          ? "Guardando..."
                          : isCreate
                          ? "Crear"
                          : "Actualizar"}
                      </button>
                    </div>
                  </Form>
                </FormikProvider>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default LawyerForm;
